import { useState } from "react";
import { Link } from "react-router-dom";
import { useTheme, useAuth } from "../../App";
import "../../pages/LandingPage.css";

/**
 * MarketingNav — the public site header, one component for every public page.
 *
 * WHY THIS EXISTS
 * The nav was written inline in LandingPage.js, so it existed on exactly one
 * URL. A reader arriving on an article from search had no way to reach
 * pricing, features or signup without going back to the homepage — on the
 * pages most likely to be an entry point.
 *
 * THE MARKUP IS COPIED VERBATIM FROM THE HOMEPAGE. Same classes, same order,
 * same SVGs, same data-testids. LandingPage now renders this component instead
 * of its own copy, which is what makes "identical on every page" structural
 * rather than a thing to keep in sync by hand.
 *
 * TWO THINGS DIFFER BY PAGE, AND ONLY TWO:
 *
 * 1. The section links. On the homepage "Features" is the #features anchor a
 *    few hundred pixels down. Everywhere else that anchor points at nothing,
 *    so the links resolve to real routes where one exists (/features,
 *    /compare, /pricing) and back to the homepage anchor where one does not
 *    (/#how, /#use-cases — there is no /use-cases index page, ruling 46).
 *
 * 2. The .tdv4 wrapper. All of this styling is scoped under .tdv4 and lives in
 *    LandingPage.css. The homepage already sits inside that wrapper; other
 *    pages do not and must not, because .tdv4 carries a whole typography and
 *    colour reset that would restyle Tailwind pages underneath it. So off the
 *    homepage the nav brings its OWN minimal wrapper, `.tdv4--chrome`, which
 *    supplies the custom properties the nav needs and cancels the full-page
 *    layout rules.
 */

const STROKE = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

/** Hash targets are same-page anchors; anything else is a router link. */
function NavLink({ to, children, onClick }) {
  if (to.startsWith("#")) {
    return (
      <a href={to} onClick={onClick}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} onClick={onClick}>
      {children}
    </Link>
  );
}

export default function MarketingNav({ home = false }) {
  const { theme, toggleTheme } = useTheme();
  const { isAuthenticated } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  const startHref = isAuthenticated ? "/dashboard" : "/auth?mode=signup";
  const dashHref = isAuthenticated ? "/dashboard" : "/auth";
  const dashLabel = isAuthenticated ? "Dashboard" : "Log in";

  const LINKS = home
    ? [
        ["#features", "Features"],
        ["#how", "How it works"],
        ["#use-cases", "Use cases"],
        ["#compare", "Compare"],
        ["#pricing", "Pricing"],
        ["/blog", "Blog"],
      ]
    : [
        ["/features", "Features"],
        ["/#how", "How it works"],
        ["/#use-cases", "Use cases"],
        ["/compare", "Compare"],
        ["/pricing", "Pricing"],
        ["/blog", "Blog"],
      ];

  const header = (
    <header className="nav">
      <div className="wrap nav__in">
        <Link to="/" className="brand" aria-label="Tadlo — home">
          <span className="brand__logo">
            <img src="/tadlo-logo.jpeg" alt="Tadlo" width="52" height="52" />
          </span>
        </Link>
        <nav aria-label="Primary">
          <ul className="nav__links">
            {LINKS.map(([to, label]) => (
              <li key={label}>
                <NavLink to={to}>{label}</NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="nav__act">
          <button
            className="iconbtn"
            type="button"
            onClick={toggleTheme}
            aria-label="Switch between light and dark mode"
          >
            <svg className="ic-moon" {...STROKE} strokeWidth={1.9}>
              <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
            </svg>
            <svg className="ic-sun" {...STROKE} strokeWidth={1.9}>
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
            </svg>
          </button>
          <Link className="nav__dash" to={dashHref} data-testid="header-login-btn">
            {dashLabel}
          </Link>
          <Link className="nav__go" to={startHref} data-testid="header-cta-btn">
            Start free
          </Link>
          <button
            className="iconbtn burger"
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="mnav"
          >
            <svg {...STROKE} strokeLinejoin={undefined}>
              <path d="M3 6h18M3 12h18M3 18h18" />
            </svg>
          </button>
        </div>
      </div>
      <nav
        className="mnav"
        id="mnav"
        data-open={menuOpen ? "true" : "false"}
        aria-label="Mobile"
      >
        <ul>
          {LINKS.map(([to, label]) => (
            <li key={label}>
              <NavLink to={to} onClick={closeMenu}>
                {label}
              </NavLink>
            </li>
          ))}
          <li>
            <Link to={dashHref} onClick={closeMenu}>
              {dashLabel}
            </Link>
          </li>
        </ul>
        <Link className="btn btn--go btn--full" to={startHref} onClick={closeMenu}>
          Start free
        </Link>
      </nav>
    </header>
  );

  if (home) return header;

  return (
    <div
      className="tdv4 tdv4--chrome"
      data-theme={theme === "dark" ? "dark" : "light"}
      data-testid="marketing-nav"
    >
      {header}
      {/* Mobile bottom bar. The homepage answers mobile with the hamburger
          above, which is fine when the reader arrived at the top of the page;
          an article reader is usually a long way down it, where a sticky
          header is off-screen and a burger they have scrolled past is no
          navigation at all. Same destinations, no new styling language — it
          reuses the nav's own custom properties. */}
      <nav className="mktbar" aria-label="Marketing">
        {LINKS.slice(0, 5).map(([to, label]) => (
          <NavLink key={label} to={to}>
            {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
